/** biome-ignore-all lint/style/noNonNullAssertion: false positive */

import {
	createDirectionKey,
	type DirectionKey,
	type OriginId,
} from '@/entities/direction';

import type { Station } from '@/entities/station';
import { isPositiveNumber } from '@/shared/lib';
import type { StationWithSaving } from '../model/stationWithSaving.schema';

type CreateStationsWithSavingsParams = {
	stations: Station[];
	originId: OriginId;
	distanceMap: Map<DirectionKey, number>;
	fuelAmount: number;
	fuelEfficiency: number;
};

const createStationsWithSavings = ({
	stations,
	originId,
	distanceMap,
	fuelAmount,
	fuelEfficiency,
}: CreateStationsWithSavingsParams): StationWithSaving[] => {
	let baseStation: Station | undefined;
	let baseDistance = Infinity;

	for (const station of stations) {
		const directionKey = createDirectionKey(originId, station.id);

		const dist = distanceMap.get(directionKey);
		if (dist && dist < baseDistance) {
			baseDistance = dist;
			baseStation = station;
		}
	}

	if (!Number.isFinite(baseDistance) || !baseStation) {
		return stations.map((station) => {
			const directionKey = createDirectionKey(originId, station.id);

			return {
				...station,
				distanceFromUser: distanceMap.get(directionKey),
				isNearestStation: false,
			};
		});
	}

	return stations.map((station) => {
		const directionKey = createDirectionKey(originId, station.id);

		const isNearestStation = station.id === baseStation!.id;
		const distanceFromUser = distanceMap.get(directionKey);

		if (
			!isPositiveNumber(fuelAmount) ||
			!isPositiveNumber(fuelEfficiency) ||
			distanceFromUser == null
		) {
			return { ...station, distanceFromUser, isNearestStation };
		}

		const extraDistance = Math.max(0, distanceFromUser - baseDistance);
		const priceSavings = (baseStation!.price - station.price) * fuelAmount;
		const extraFuelCost =
			(extraDistance / 1000 / fuelEfficiency) * station.price;

		return {
			...station,
			distanceFromUser,
			extraDistance,
			isNearestStation,
			savings: Math.floor(priceSavings - extraFuelCost),
		};
	});
};

export default createStationsWithSavings;
